import { readErrorMessage } from '@Components/util/utils'
import { PlayerServiceAPI } from '@API/data-services/player-service'
import { PamPlayerSearchSummary } from '@Generated/back-office-client/src'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Skeleton,
  TextField,
  Text,
} from '@radix-ui/themes'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import './styles.scss'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store'
import { Routes } from '@Components/main-app/types'
import { usePlayerCapabilities } from './use-player-capabilities'
import { PlayerSearchListView } from './player-search-list-view'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 3

export const PlayerSearch = () => {
  const { data: capabilities, isLoading } = usePlayerCapabilities()

  if (isLoading) {
    return (
      <Container className="player-search-container">
        <Section>
          <Skeleton loading>
            <Heading as="h1" size="5">
              Player Lookup
            </Heading>
          </Skeleton>
        </Section>
      </Container>
    )
  }

  if (capabilities?.isEditable) {
    return <PamPlayerSearch />
  }

  return <VendorPlayerSearch />
}

const PamPlayerSearch = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const nav = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const committedSearch = searchParams.get('search') ?? ''
  const parsedPage = Number(searchParams.get('page') ?? '0')
  const currentPage = Number.isNaN(parsedPage) ? 0 : parsedPage

  const [searchInput, setSearchInput] = useState(committedSearch)

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchInput.trim()
      if (trimmed === (searchParams.get('search') ?? '')) {
        return
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (trimmed) {
            next.set('search', trimmed)
          } else {
            next.delete('search')
          }
          next.set('page', '0')
          return next
        },
        { replace: true },
      )
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchInput, searchParams, setSearchParams])

  const effectiveSearch = committedSearch.length >= MIN_SEARCH_LENGTH ? committedSearch : ''

  const { data, isLoading } = useQuery({
    queryKey: [`${activeCustomerId}-player-search`, effectiveSearch, currentPage],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    enabled: effectiveSearch !== '',
    queryFn: () =>
      PlayerServiceAPI.searchPlayers(
        effectiveSearch,
        currentPage * PAGE_SIZE,
        currentPage * PAGE_SIZE + PAGE_SIZE,
      ),
  })

  const onSearchChange = (next: string) => {
    setSearchInput(next)
  }

  const onPageChange = (page: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('page', String(page))
        return next
      },
      { replace: true },
    )
  }

  const currentSearchHref = `${Routes.PLAYER_SEARCH}?${searchParams.toString()}`

  const onRowClick = (row: PamPlayerSearchSummary) => {
    nav(Routes.PLAYER_DETAILS.replace(':lookupId', row.lookupId), {
      state: { from: currentSearchHref },
    })
  }

  return (
    <Container className="player-search-container">
      <Section>
        <Flex direction="column" gap="4">
          <Heading as="h1" size="5">
            Player Lookup
          </Heading>
          <Heading as="h2" size="3">
            Search for a player by name, email, or id.
          </Heading>
          <PlayerSearchListView
            page={effectiveSearch === '' ? undefined : data}
            search={searchInput}
            minSearchLength={MIN_SEARCH_LENGTH}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            currentSearchHref={currentSearchHref}
            isLoading={isLoading && effectiveSearch !== ''}
            onSearchChange={onSearchChange}
            onPageChange={onPageChange}
            onRowClick={onRowClick}
          />
        </Flex>
      </Section>
    </Container>
  )
}

const VendorPlayerSearch = () => {
  const nav = useNavigate()
  const [playerSearchId, setPlayerSearchId] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string | null>()

  const { mutate: searchPlayer, isPending } = useMutation({
    mutationFn: async () => {
      setErrorMessage(null)
      try {
        const details = await PlayerServiceAPI.getPlayerByLookup(playerSearchId)
        nav(Routes.PLAYER_DETAILS.replace(':lookupId', details.player.lookupId), {
          state: details,
        })
      } catch (e) {
        // readErrorMessage covers the non-HTTP case, so a network failure no longer goes unreported.
        setErrorMessage(await readErrorMessage(e))
      }
    },
  })

  return (
    <Container className="player-search-container">
      <Section>
        <Flex direction="column" gap="3">
          <Heading as="h1" size="5">
            Player Lookup
          </Heading>
          <Heading as="h2" size="3">
            Search for a player to locate their details and activity.
          </Heading>

          <Flex gap="3">
            <TextField.Root
              className="player-id-lookup-field"
              placeholder="Search for a player by id"
              value={playerSearchId}
              onChange={(e) => {
                setPlayerSearchId(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && playerSearchId) {
                  searchPlayer()
                }
              }}>
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>

            <Button
              disabled={!playerSearchId}
              onClick={() => searchPlayer()}
              loading={!!playerSearchId && isPending}>
              Search
            </Button>

            <Button
              disabled={!playerSearchId}
              onClick={() => setPlayerSearchId('')}
              color="gray"
              variant="soft">
              Clear
            </Button>
          </Flex>

          {errorMessage ? <Text color="red">{errorMessage}</Text> : null}
        </Flex>
      </Section>
    </Container>
  )
}
